import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  HStack,
  Button,
  Image,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  VStack,
  Text,
  useDisclosure,
  useColorModeValue,
} from "@chakra-ui/react";
import { Link, useLocation } from "react-router-dom";
import { HamburgerIcon } from "@chakra-ui/icons";

function Navbar() {
  const location = useLocation();
  const [activeLink, setActiveLink] = useState("/");

  useEffect(() => setActiveLink(location.pathname), [location.pathname]);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const links = useMemo(
    () => [
      { to: "/", label: "Home" },
      { to: "/about-us", label: "Team" },
      { to: "/program", label: "Program" },
      { to: "/seminars", label: "Seminars" },
      { to: "/publications", label: "Publications" },
      { to: "/news", label: "News" },
      /*{ to: "/contact-us", label: "Contact" },*/
    ],
    []
  );

  const bg = useColorModeValue("rgba(226,225,220,0.85)", "rgba(16,16,16,0.75)");
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const text = useColorModeValue("#315369", "white");
  const accent = "#DC242C";

  const NavLink = ({ to, label, onClick }) => {
    const isActive = activeLink === to;
    return (
      <Button
        as={Link}
        to={to}
        onClick={onClick}
        variant="ghost"
        size="sm"
        fontWeight="800"
        letterSpacing="0.06em"
        textTransform="uppercase"
        color={isActive ? accent : text}
        position="relative"
        _hover={{ bg: "transparent", color: accent }}
        _active={{ bg: "transparent" }}
        px={3}
      >
        {label}
        <Box
          position="absolute"
          left="10%"
          right="10%"
          bottom="-6px"
          height="2px"
          borderRadius="999px"
          bg={accent}
          opacity={isActive ? 1 : 0}
          transform={isActive ? "scaleX(1)" : "scaleX(0.6)"}
          transition="all 180ms ease"
        />
      </Button>
    );
  };

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={50}
      bg={bg}
      backdropFilter="blur(10px)"
      borderBottom="1px solid"
      borderColor={border}
    >
      {/* Top row */}
      <Box maxW="1200px" mx="auto" px={{ base: 4, md: 10 }} py={{ base: 3, md: 4 }}>
        <Flex align="center" justify="space-between" gap={4}>
          {/* Brand */}
          <HStack spacing={3} minW="fit-content">
            <Image
              src="header-logo.png"
              alt="Lab logo"
              w={{ base: "44px", md: "60px" }}
              h={{ base: "44px", md: "60px" }}
              objectFit="contain"
            />
            <Box lineHeight={1.05}>
              <Text fontWeight="900" fontSize={{ base: "md", md: "lg" }} color={text}>
                SITE-CAV
              </Text>
              <Text fontSize="sm" color={useColorModeValue("gray.600", "gray.300")}>
                Connected & Autonomous Vehicles
              </Text>
            </Box>
          </HStack>

          {/* Desktop nav */}
          <HStack spacing={1} display={{ base: "none", md: "flex" }}>
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} label={l.label} />
            ))}
          </HStack>

          {/* Right side */}
          <HStack spacing={2}>
            {/* Optional CTA */}
            <Button
              as={Link}
              to="/contact-us"
              size="sm"
              display={{ base: "none", lg: "inline-flex" }}
              bg={accent}
              color="white"
              fontWeight="800"
              _hover={{ filter: "brightness(0.95)" }}
            >
              Contact Us
            </Button>

            {/* Mobile menu button */}
            <IconButton
              aria-label="Open menu"
              icon={<HamburgerIcon />}
              variant="ghost"
              display={{ base: "inline-flex", md: "none" }}
              onClick={onOpen}
            />
          </HStack>
        </Flex>
      </Box>

      {/* Bottom ribbon (your long title) */}
      <Box
        borderTop="1px solid"
        borderColor={border}
        bg={useColorModeValue("rgba(255,255,255,0.35)", "rgba(255,255,255,0.06)")}
      >
        <Box maxW="1200px" mx="auto" px={{ base: 4, md: 10 }} py={2}>
          <Text
            fontSize={{ base: "xs", md: "sm" }}
            fontWeight="700"
            color={useColorModeValue("gray.700", "gray.200")}
            letterSpacing="0.02em"
            textAlign={{ base: "left", md: "center" }}
          >
            SECURE, INTELLIGENT AND TRUSTWORTHY ECOSYSTEMS FOR CONNECTED AND AUTONOMOUS VEHICLES (SITE-CAV)
          </Text>
        </Box>
      </Box>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Menu</DrawerHeader>
          <DrawerBody>
            <VStack align="stretch" spacing={1} mt={2}>
              {links.map((l) => (
                <Button
                  key={l.to}
                  as={Link}
                  to={l.to}
                  onClick={onClose}
                  justifyContent="flex-start"
                  variant="ghost"
                  fontWeight="800"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                  color={activeLink === l.to ? accent : text}
                >
                  {l.label}
                </Button>
              ))}
              <Button
                as={Link}
                to="/contact-us"
                onClick={onClose}
                mt={3}
                bg={accent}
                color="white"
                fontWeight="900"
                _hover={{ filter: "brightness(0.95)" }}
              >
                Contact Us
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}

export default Navbar;
